/**
 * Share-card size-class selection.
 *
 * Pure helpers. No React, no Supabase, no i18n. Probe-importable.
 *
 * Ship 2a.3 retired all truncation; verses now render in full thanks
 * to verse length being intrinsically bounded by the picker (single-
 * verse and short-range picks). `capOnWordBoundary` and the
 * `VERSE_CHAR_BUDGET` constant were deleted along with the CSS
 * `-webkit-line-clamp` rule that backed them. Filename retained as
 * historical scar; a rename has no functional benefit and would
 * needlessly churn imports.
 *
 * `pickCardSize` is now the entire surface. It picks the verse size
 * class so long verses render at a smaller font and stay inside the
 * canvas-safe bound. Previously also factored in spine-catalog
 * `display_prose` length (Ship 2a → 2a.3), but the spine-story
 * consumer was retired from the share card on 2026-05-23 — see
 * CLAUDE.md rule #77 parallel.
 */

export type CardSize = 'lg' | 'md' | 'sm';

/**
 * Pick the verse size class based on verse character length.
 *
 * Canvas-safety derivation (iOS Safari ~16,384 device-px / 8,192
 * layout-px dim limit, less ~1,100 layout px of chrome overhead):
 *
 *   lg: ≤120 chars  (single-verse, short)
 *   md: ≤240 chars  (single-verse, longer or two-verse)
 *   sm: >240 chars  (multi-verse range)
 *
 * Important: caller must pass EFFECTIVE verse length (post Include /
 * Skip toggle), NOT raw picker selection. A skipped verse should
 * produce the lg fallback (effective length 0) so the size class
 * stays stable when verses come and go through Prev/revise.
 */
export function pickCardSize(verseLength: number): CardSize {
  if (verseLength <= 120) return 'lg';
  if (verseLength <= 240) return 'md';
  return 'sm';
}
